/**
 * Staffelkorting (quantity/volume discount) voor losse catalogus-artikelen.
 *
 * Timmers levert per artikel een `bulk`-staffel: `[{amount, priceCents}]`, oplopend op
 * `amount` (bv. vanaf 1 stuk, vanaf 10 stuks) met een DALENDE stuksprijs. Die tier-prijzen
 * staan op de TIMMERS-schaal (inkoop × dealer-korting × marge) — NIET op onze admin-basisprijs.
 * We gebruiken daarom niet de absolute tier-prijs, maar de KORTING-RATIO t.o.v. de
 * gesynchroniseerde basisprijs (`refBaseCents` = `article.priceCents`): de dealer-korting en de
 * globale marge zitten identiek in de tier-prijs én in de basis, dus ze vallen in die ratio weg
 * en er blijft puur het STAFFELKORTING-PERCENTAGE over. Dat percentage passen we toe op ONZE
 * basisprijs (`baseCents` — die is incl. admin-override/marge/actie). Zo blijft de door de admin
 * ingestelde basisprijs leidend en krijgt de klant het juiste volume-percentage eroverheen
 * (i.p.v. dat de kale Timmers-inkoopprijs de basisprijs overschrijft).
 *
 * Client (productpagina) én server (createOrder) gebruiken 'm zodat de getoonde prijs en de
 * daadwerkelijk berekende orderprijs identiek zijn.
 */
export interface BulkTier {
  amount: number;
  priceCents: number;
}

/**
 * Stuksprijs van één tier op ONZE basisprijs: pas de staffelkorting-ratio (tier ÷ gesynchroniseerde
 * basis) toe op `baseCents`. Zonder bruikbare referentie of tier-prijs valt 'ie terug op `baseCents`
 * (dan is er geen korting) — nooit duurder dan de basisprijs.
 */
export function bulkTierUnitCents(baseCents: number, refBaseCents: number, tier: BulkTier): number {
  if (!(refBaseCents > 0) || !(tier.priceCents > 0)) return baseCents;
  return Math.min(baseCents, Math.round(baseCents * (tier.priceCents / refBaseCents)));
}

/**
 * Effectieve stuksprijs bij een gegeven aantal: de laagste tier waarvan `amount ≤ qty`, met de
 * staffelkorting-ratio toegepast op onze basisprijs (`baseCents`). `refBaseCents` is de
 * gesynchroniseerde artikelprijs waartegen de tier-prijzen zijn berekend (`article.priceCents`).
 */
export function bulkUnitCents(baseCents: number, refBaseCents: number, bulk: BulkTier[] | undefined, qty: number): number {
  if (!bulk || bulk.length === 0) return baseCents;
  let unit = baseCents;
  for (const tier of bulk) {
    if (qty >= tier.amount && tier.priceCents > 0) unit = Math.min(unit, bulkTierUnitCents(baseCents, refBaseCents, tier));
  }
  return unit;
}

/**
 * Wat WIJ bij Timmers per stuk betalen bij dit aantal: de ABSOLUTE tier-prijs, niet de ratio.
 *
 * Het onderscheid met {@link bulkUnitCents} is de kern. Voor de VERKOOPprijs gebruiken we de
 * korting-ratio, omdat onze eigen basisprijs (admin-override, marge, actie) leidend moet blijven.
 * Voor de INKOOPprijs geldt dat niet: Timmers rekent gewoon het bedrag dat in hun staffel staat.
 *
 * Stond hier eerder `article.priceCents` (de losse stuksprijs), waardoor we bij een order van 3+
 * stuks een te hoge inkoop vastlegden: de bestelbon vroeg Timmers om meer dan ze factureren, en
 * de marge in de admin oogde lager dan hij is. Bij order 1025 scheelde dat € 14,55 op één regel
 * en 7,4 procentpunt marge.
 */
export function bulkSupplierUnitCents(refBaseCents: number, bulk: BulkTier[] | undefined, qty: number): number {
  if (!bulk || bulk.length === 0) return refBaseCents;
  let unit = refBaseCents;
  for (const tier of bulk) {
    if (qty >= tier.amount && tier.priceCents > 0) unit = Math.min(unit, tier.priceCents);
  }
  return unit;
}
